import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/joy';
import {
  Add,
  CalendarMonth,
  EventNote,
  Group,
  Notifications,
  PendingActions,
  RateReview,
  Schedule,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { dashboardService } from '@/services/dashboardService';
import { noteService } from '@/services/noteService';
import type {
  ManagerDashboardData,
  PreferenceAlert,
  ProjectOverviewEntry,
} from '@/types/dashboard';
import WelcomeHeader from '@/components/dashboard/WelcomeHeader';
import { useDashboardRefresh } from '@/components/dashboard/useDashboardRefresh';
import StatCard from '@/components/dashboard/StatCard';
import WidgetPanel from '@/components/dashboard/WidgetPanel';
import ScheduleListItem from '@/components/dashboard/ScheduleListItem';
import ProjectCard from '@/components/dashboard/ProjectCard';
import NotificationWidget from '@/components/NotificationWidget';

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<ManagerDashboardData | null>(null);
  const [preferenceAlerts, setPreferenceAlerts] = useState<PreferenceAlert[]>([]);
  const [projects, setProjects] = useState<ProjectOverviewEntry[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashData, alerts, projectsData, notesData] = await Promise.all([
        dashboardService.getManagerDashboard(),
        dashboardService.getPreferenceAlerts().catch(() => []),
        dashboardService.getProjectsOverview(5).catch(() => []),
        noteService.getNotes({ limit: 5 }).catch(() => ({ notes: [] })),
      ]);
      setData(dashData);
      setPreferenceAlerts(alerts);
      setProjects(projectsData);
      setNotes(Array.isArray(notesData) ? notesData : notesData.notes || []);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDashboardRefresh(fetchData);

  return (
    <Box>
      <WelcomeHeader onRefresh={fetchData} />

      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 1.5 }}>Quick Actions</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startDecorator={<Schedule />} onClick={() => navigate('/schedule')}>
              Create Schedule
            </Button>
            <Button variant="outlined" startDecorator={<Add />} onClick={() => navigate('/projects')}>
              Create Project
            </Button>
            <Button variant="outlined" startDecorator={<RateReview />} onClick={() => navigate('/change-requests')}>
              Review Requests
            </Button>
            <Button variant="outlined" startDecorator={<CalendarMonth />} onClick={() => navigate('/calendar')}>
              View Team Calendar
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Today's Snapshot Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<Group sx={{ color: 'white' }} />}
            value={data?.todayStats.employees_on_shift ?? '-'}
            label="Employees on Shift Today"
            color="primary.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<PendingActions sx={{ color: 'white' }} />}
            value={data?.todayStats.pending_change_requests ?? '-'}
            label="Pending Change Requests"
            color="warning.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<EventNote sx={{ color: 'white' }} />}
            value={data?.todayStats.upcoming_schedules_week ?? '-'}
            label="Schedules This Week"
            color="success.500"
            loading={loading}
          />
        </Grid>
        <Grid xs={6} md={3}>
          <StatCard
            icon={<Notifications sx={{ color: 'white' }} />}
            value={data?.todayStats.unread_notifications ?? '-'}
            label="Unread Notifications"
            color="info.500"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid xs={12} md={6}>
          <Stack spacing={3}>
            {/* Today's Schedule Overview */}
            <WidgetPanel
              title="Today's Schedule"
              loading={loading}
              isEmpty={!data?.todaySchedule.length}
              emptyMessage="No shifts scheduled for today"
              onViewAll={() => navigate('/calendar')}
              viewAllLabel="View Calendar"
            >
              <Stack spacing={1}>
                {data?.todaySchedule.map(entry => (
                  <ScheduleListItem key={entry.id} entry={entry} showEmployee />
                ))}
              </Stack>
            </WidgetPanel>

            {/* Change Request Queue */}
            <WidgetPanel
              title="Change Request Queue"
              loading={loading}
              isEmpty={!data?.changeRequestQueue.length}
              emptyMessage="No pending requests — all caught up!"
              onViewAll={() => navigate('/change-requests')}
              viewAllLabel="Review All"
            >
              <Stack spacing={1}>
                {data?.changeRequestQueue.map(cr => (
                  <Box
                    key={cr.id}
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography level="body-sm" fontWeight="lg">
                          {cr.employee.first_name} {cr.employee.last_name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {cr.request_type.replace('_', ' ')} &middot; {new Date(cr.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={cr.priority === 'high' ? 'danger' : cr.priority === 'normal' ? 'warning' : 'neutral'}
                        >
                          {cr.priority}
                        </Chip>
                        <Chip size="sm" variant="soft" color="warning">
                          {cr.status}
                        </Chip>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="sm"
                        color="success"
                        variant="soft"
                        onClick={() => navigate(`/change-requests`)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        variant="soft"
                        onClick={() => navigate(`/change-requests`)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </WidgetPanel>

            {/* Preference Alerts */}
            <WidgetPanel
              title="Employee Preference Alerts"
              loading={loading}
              isEmpty={!preferenceAlerts.length}
              emptyMessage="No preference conflicts detected"
            >
              <Stack spacing={1}>
                {preferenceAlerts.map((alert, idx) => (
                  <Box
                    key={idx}
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.300', borderRadius: 'sm', bgcolor: 'warning.50' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Warning color="warning" fontSize="small" />
                      <Box>
                        <Typography level="body-sm" fontWeight="lg">
                          {alert.employee_first_name} {alert.employee_last_name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {alert.conflict_detail} &middot; {alert.shift_type_name} on{' '}
                          {new Date(alert.schedule_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </WidgetPanel>
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid xs={12} md={6}>
          <Stack spacing={3}>
            {/* Upcoming Week Preview */}
            <WidgetPanel
              title="Upcoming Week"
              loading={loading}
              isEmpty={!data?.weekPreview.length}
              emptyMessage="No schedule data for the week"
            >
              <Stack spacing={1}>
                {data?.weekPreview.map(day => (
                  <Box
                    key={day.date}
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: day.scheduled_count === 0 ? 'warning.300' : 'divider',
                      borderRadius: 'sm',
                      bgcolor: day.scheduled_count === 0 ? 'warning.50' : undefined,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography level="body-sm" fontWeight="lg">
                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {day.shift_types.map(st => (
                            <Chip
                              key={st.name}
                              size="sm"
                              sx={{
                                bgcolor: st.color,
                                color: 'white',
                                '--Chip-minHeight': '18px',
                                fontSize: '0.7rem',
                              }}
                            >
                              {st.name}: {st.count}
                            </Chip>
                          ))}
                        </Stack>
                      </Box>
                      <Stack alignItems="flex-end">
                        <Typography level="body-sm" fontWeight="lg">
                          {day.scheduled_count}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          scheduled
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </WidgetPanel>

            {/* Team Notes */}
            <WidgetPanel
              title="Team Notes"
              loading={loading}
              isEmpty={!notes.length}
              emptyMessage="No recent notes"
              onViewAll={() => navigate('/notes')}
              action={
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<Add />}
                  onClick={() => navigate('/notes')}
                >
                  New Note
                </Button>
              }
            >
              <Stack spacing={1}>
                {notes.slice(0, 5).map((note: any) => (
                  <Box
                    key={note.id}
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography level="body-sm" fontWeight="lg" noWrap>
                        {note.title || note.content?.substring(0, 40) || 'Note'}
                      </Typography>
                      {note.priority === 'high' && (
                        <Chip size="sm" color="danger" variant="soft">important</Chip>
                      )}
                    </Stack>
                    <Typography level="body-xs" color="neutral" noWrap>
                      {note.content?.substring(0, 80)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </WidgetPanel>

            {/* Active Projects */}
            <WidgetPanel
              title="Active Projects"
              loading={loading}
              isEmpty={!projects.length}
              emptyMessage="No active projects. Create one to get started!"
              onViewAll={() => navigate('/projects')}
              viewAllLabel="View All Projects"
            >
              <Box>
                {projects.map(project => (
                  <ProjectCard key={project.id} project={project} expandable />
                ))}
              </Box>
            </WidgetPanel>

            {/* Notifications */}
            <NotificationWidget maxItems={5} />
          </Stack>
        </Grid>
      </Grid>

    </Box>
  );
}
